<template>
  <div
    class="grid w-full grid-cols-[fit-content(100%)_minmax(auto,100%)] grid-rows-[fit-content(0)] max-md:grid-cols-1 max-md:grid-rows-1"
    data-testid="product-gallery"
  >
    <SFItemsSlider
      with-arrows
      mode="vertical"
      class="mr-4 w-24 shrink-0 max-md:hidden"
      data-testid="product-thumbnails"
    >
      <div
        v-for="(productThumbnail, index) in images"
        :key="productThumbnail.hash"
        :data-testid="`product-thumbnail-${index}`"
        class="my-2 aspect-square h-[130px] w-24 shrink-0 cursor-pointer overflow-hidden rounded-md bg-gray-50 first:mt-0 last:mb-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        :class="index === activeSlide ? 'bg-gray-900/10' : 'bg-gray-50'"
        tabindex="0"
        role="button"
        :aria-label="
          $t('product_image.thumbnail_alt', {
            index: index + 1,
            total: images.length,
          })
        "
        :aria-pressed="index === activeSlide"
        @mouseenter="scrollImageIntoView(index)"
        @click="isZoomModalOpen = true"
        @keydown.enter="isZoomModalOpen = true"
        @keydown.space.prevent="isZoomModalOpen = true"
      >
        <SFPDPGalleryImage
          :image="productThumbnail"
          :alt="
            $t('product_image.alt_with_image_index', {
              alt,
              index: index + 1,
              total: images.length,
            })
          "
          fit="cover"
          :width="192"
          :height="260"
          :aspect-ratio="[192, 260]"
          :product-path="productPath"
          sizes="192px"
        />
      </div>
      <template #prev-button="{ prev, isPrevEnabled }">
        <button
          class="absolute left-1/2 top-2 size-8 -translate-x-1/2 rounded-md bg-white/85 p-1 text-gray-500 hover:text-gray-900 disabled:hidden"
          :aria-label="$t('image_slider.a11ly.go_to_previous_image')"
          :disabled="!isPrevEnabled"
          @click="prev()"
        >
          <IconChevronUp class="size-6 p-0.5" />
        </button>
      </template>
      <template #next-button="{ next, isNextEnabled }">
        <button
          class="absolute bottom-2 left-1/2 size-8 -translate-x-1/2 rounded-md bg-white/85 p-1 text-gray-500 hover:text-gray-900 disabled:hidden"
          :aria-label="$t('image_slider.a11ly.go_to_next_image')"
          :disabled="!isNextEnabled"
          @click="next()"
        >
          <IconChevronDown class="size-6 p-0.5" />
        </button>
      </template>
    </SFItemsSlider>
    <div class="relative">
      <SFItemsSlider
        id="image"
        ref="image"
        :with-arrows="images.length > 1"
        :slider-class="productImageSliderClass"
        data-testid="main-product-image"
        :slider-tabindex="-1"
        @update:active-slide="updateActiveSlide"
      >
        <SFPDPGalleryImage
          v-for="(productImage, index) in images"
          :key="productImage.hash"
          :image="productImage"
          :image-loading="index === 0 ? 'eager' : 'lazy'"
          :preload="index === 0"
          :alt="
            $t('product_image.alt_with_image_index', {
              alt,
              index: index + 1,
              total: images.length,
            })
          "
          :aspect-ratio="[3, 4]"
          fit="cover"
          :data-testid="`product-image-${index}`"
          sizes="xs:100vw sm:100vw md:50vw lg:50vw xl:50vw"
          class="min-w-full cursor-pointer snap-start snap-always self-start overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 md:rounded-md"
          tabindex="0"
          role="button"
          :aria-label="
            $t('product_image.main_image_alt', {
              index: index + 1,
              total: images.length,
            })
          "
          :product-path="productPath"
          @click="isZoomModalOpen = true"
          @keydown.enter="isZoomModalOpen = true"
          @keydown.space.prevent="isZoomModalOpen = true"
        />
        <template #arrows="{ prev, isPrevEnabled, next, isNextEnabled }">
          <div class="absolute bottom-4 right-4 flex space-x-2 max-md:hidden">
            <SFButton
              class="size-12 rounded-md border-none bg-white"
              size="md"
              fab
              variant="raw"
              :aria-label="$t('image_slider.a11ly.go_to_previous_image')"
              :disabled="!isPrevEnabled"
              direction="left"
              @click="prev()"
            >
              <template #icon>
                <IconChevronLeft class="size-6 text-gray-700" />
              </template>
            </SFButton>

            <SFButton
              class="size-12 rounded-md border-none bg-white"
              size="md"
              fab
              variant="raw"
              :aria-label="$t('image_slider.a11ly.go_to_next_image')"
              :disabled="!isNextEnabled"
              direction="right"
              @click="next()"
            >
              <template #icon>
                <IconChevronRight class="size-6 text-gray-700" />
              </template>
            </SFButton>
          </div>
        </template>
        <template #thumbnails>
          <div
            class="absolute bottom-4 left-1/2 flex -translate-x-1/2 space-x-1 md:hidden"
          >
            <div
              v-for="imageIndex in images.length"
              :key="imageIndex"
              class="size-1 rounded-full bg-gray-300 transition-all duration-300"
              :class="{ 'w-3 !bg-black': imageIndex - 1 === activeSlide }"
            />
          </div>
        </template>
      </SFItemsSlider>
      <div class="absolute left-0 top-3 flex w-full justify-between px-4 py-3">
        <div class="flex items-center text-gray-700">
          <SFGoBackLink class="md:hidden" :fallback-link="categoryLink" />
        </div>
        <div class="relative flex w-fit items-center space-x-3 text-gray-700">
          <SFButton variant="raw" class="hidden !text-gray-700">
            <IconShare class="size-6" />
          </SFButton>
        </div>
      </div>
      <SFProductBadges
        :product="product"
        class="absolute left-5 hidden max-md:bottom-5 md:left-10 md:top-10 md:block"
      />
    </div>
  </div>
  <SFProductGalleryZoom
    v-model:visible="isZoomModalOpen"
    :alt="alt"
    :images="images"
    :start-index="activeSlide"
    @close="isZoomModalOpen = false"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Product } from '@scayle/storefront-nuxt'
import SFProductBadges from '../../card/SFProductBadges.vue'
import SFPDPGalleryImage from './SFPDPGalleryImage.vue'
import SFProductGalleryZoom from './SFProductGalleryZoom.vue'

import {
  SFButton,
  SFGoBackLink,
  SFItemsSlider,
} from '#storefront-ui/components'

import { useProductBaseInfo, useRouteHelpers } from '~/composables'
import { useRoute } from '#app/composables/router'

const { product, productImageSliderClass } = defineProps<{
  product: Product
  productImageSliderClass?: string
}>()

const route = useRoute()

// Extract product path from current route for production URLs
const productPath = computed(() => {
  const pathSegments = route.path.split('/').filter(Boolean)
  const pIndex = pathSegments.findIndex((segment) => segment === 'p')
  return pIndex !== -1 && pathSegments[pIndex + 1]
    ? pathSegments[pIndex + 1]
    : ''
})

const { buildCategoryPath } = useRouteHelpers()
const categoryLink = computed(() => {
  const category = longestCategoryList.value.at(-1)
  if (!category) {
    return
  }

  return buildCategoryPath({
    id: category.categoryId,
    path: category.categoryUrl,
  })
})

const image = ref()

const scrollImageIntoView = (index: number) => {
  image.value?.scrollImageIntoView(index, 'smooth')
  activeSlide.value = index
}

const activeSlide = ref(0)
const updateActiveSlide = (newSlide: number) => {
  activeSlide.value = Number.isFinite(newSlide) ? newSlide : 0
}

const isZoomModalOpen = ref(false)

const { alt, images, longestCategoryList } = useProductBaseInfo(() => product)
</script>
